import React, { useState, useEffect, useCallback } from 'react';
import {
    Send, CheckCircle2, AlertTriangle, Clock, X, RefreshCw, ShieldOff,
    CalendarClock, ChevronDown, ChevronRight, Landmark,
} from 'lucide-react';
import api from '../services/api';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import '../styles/SistemaTS.css';

/**
 * SistemaTS — cosa è partito verso l'Agenzia delle Entrate.
 *
 * L'invio avviene da solo quando la fattura viene saldata: questa
 * schermata serve a vedere cosa NON è partito e perché. È la rete di
 * sicurezza, non il modo normale di trasmettere.
 *
 * L'ambiente è scritto in alto a caratteri grandi: sapere se si sta
 * lavorando in prova o sul serio non deve richiedere di indovinare.
 */

const euro = (v) => (v == null ? '—' : Number(v).toLocaleString('it-IT', {
    style: 'currency', currency: 'EUR',
}));

const data = (d) => (d ? new Date(d).toLocaleDateString('it-IT') : '—');

const primoDelMese = () => {
    const o = new Date();
    return new Date(o.getFullYear(), o.getMonth(), 1).toISOString().slice(0, 10);
};

const SistemaTS = () => {
    const [dal, setDal] = useState(primoDelMese());
    const [al, setAl] = useState(new Date().toISOString().slice(0, 10));
    const [dati, setDati] = useState(null);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [inCorso, setInCorso] = useState(null);      // id fattura in trasmissione
    const [dettaglio, setDettaglio] = useState(null);  // fattura aperta
    const [resoconti, setResoconti] = useState([]);
    const [resocontoAperto, setResocontoAperto] = useState(null);

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const r = await api.get('/sistema-ts/stato', { params: { dal, al } });
            setDati(r.data.data);
            setErrore(null);
        } catch (e) {
            setErrore(e.response?.data?.message || 'Non riesco a leggere lo stato degli invii');
        } finally {
            setCaricamento(false);
        }
    }, [dal, al]);

    useEffect(() => { carica(); }, [carica]);

    /* I resoconti delle trasmissioni mensili non dipendono dal periodo
       scelto in alto: sono la storia del lavoro automatico, e si
       caricano una volta. */
    useEffect(() => {
        api.get('/sistema-ts/resoconti', { params: { limite: 24 } })
            .then((r) => setResoconti(r.data?.data?.resoconti || []))
            .catch(() => setResoconti([]));
    }, []);

    const invia = async (id) => {
        setInCorso(id);
        setErrore(null);
        try {
            const r = await api.post(`/sistema-ts/invia/${id}`);
            if (!r.data.success) {
                setErrore(`Fattura non trasmessa: ${(r.data.data?.messaggi || [])
                    .map((m) => m.descrizione).join('; ')}`);
            }
            await carica();
        } catch (e) {
            const p = e.response?.data?.data?.problemi;
            setErrore(p ? `Non è pronta: ${p.join('; ')}`
                : (e.response?.data?.message || 'Errore nella trasmissione'));
        } finally {
            setInCorso(null);
        }
    };

    const apriDettaglio = async (f) => {
        try {
            const [v, s] = await Promise.all([
                api.get(`/sistema-ts/verifica/${f.id}`),
                api.get(`/sistema-ts/storico/${f.id}`),
            ]);
            setDettaglio({ fattura: f, verifica: v.data.data, storico: s.data.data });
        } catch (e) {
            setErrore('Non riesco a leggere il dettaglio');
        }
    };

    const statoDi = (f) => {
        if (f.invio_ts) return { classe: 'ok', testo: 'Trasmessa', Icona: CheckCircle2 };
        if (f.ultimo_errore) return { classe: 'errore', testo: 'Errore di rete', Icona: AlertTriangle };
        if (f.ultimo_esito === 1) return { classe: 'errore', testo: 'Scartata', Icona: AlertTriangle };
        if (f.ultimo_esito === 2) return { classe: 'avviso', testo: 'Con segnalazioni', Icona: AlertTriangle };
        if (f.ultimo_tentativo) return { classe: 'attesa', testo: 'Solo in prova', Icona: Clock };
        return { classe: 'attesa', testo: 'Da trasmettere', Icona: Clock };
    };

    const r = dati?.riepilogo;

    return (
        <div className="ts-wrap">
            {/* «Andamento» e' il riquadro di Amministrazione da cui si
                arriva qui: e' una sezione che esiste davvero, non una
                categoria inventata per riempire la riga. */}
            <IntestazioneSezione
                etichetta="Andamento"
                titolo="Sistema Tessera Sanitaria"
                icona={<Landmark size={20} aria-hidden="true" />}
                sottotitolo="Le fatture partono da sole quando vengono saldate. Qui si vede cosa non è partito."
                azione={
                    <div className="ts-azioni">
                        <input type="date" value={dal} onChange={(e) => setDal(e.target.value)} />
                        <input type="date" value={al} onChange={(e) => setAl(e.target.value)} />
                        <button className="ts-btn" onClick={carica} disabled={caricamento}>
                            <RefreshCw size={15} /> Aggiorna
                        </button>
                    </div>
                }
            />

            {dati?.ambiente === 'test' && (
                <div className="ts-ambiente">
                    <ShieldOff size={16} />
                    <span>
                        <b>Ambiente di prova.</b> Gli invii da qui non valgono come
                        trasmissione: l&apos;Agenzia delle Entrate non riceve nulla.
                    </span>
                </div>
            )}

            {errore && <div className="ts-errore">{errore}</div>}

            {r && (
                <section className="ts-riepilogo">
                    <div className="ts-conta">
                        <b>{r.totali}</b><span>fatture nel periodo</span>
                    </div>
                    <div className="ts-conta ok">
                        <b>{r.inviate}</b><span>trasmesse</span>
                    </div>
                    <div className={`ts-conta ${r.scartate ? 'errore' : ''}`}>
                        <b>{r.scartate}</b><span>scartate</span>
                    </div>
                    <div className={`ts-conta ${r.mai_tentate ? 'attesa' : ''}`}>
                        <b>{r.mai_tentate}</b><span>mai tentate</span>
                    </div>
                </section>
            )}

            <ResocontiMensili
                resoconti={resoconti}
                aperto={resocontoAperto}
                onApri={setResocontoAperto}
            />

            {caricamento && !dati && <p className="ts-vuoto">Caricamento…</p>}

            {dati && !dati.fatture.length && (
                <p className="ts-vuoto">Nessuna fattura emessa nel periodo.</p>
            )}

            {dati && dati.fatture.length > 0 && (
                <div className="rm-scorre">
                <table className="ts-tabella">
                    <thead>
                        <tr>
                            <th>Fattura</th>
                            <th>Data</th>
                            <th>Intestatario</th>
                            <th className="destra">Totale</th>
                            <th>Stato</th>
                            <th>Protocollo</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {dati.fatture.map((f) => {
                            const s = statoDi(f);
                            const opposizione = f.opposizione_ts || f.opposizione_paziente;
                            return (
                                <tr key={f.id} onClick={() => apriDettaglio(f)}>
                                    <td><b>{f.numero}/{f.anno}</b></td>
                                    <td>{data(f.data_documento)}</td>
                                    <td>
                                        {f.intestatario}
                                        {opposizione && <em className="ts-nota">opposizione</em>}
                                        {!opposizione && !f.ha_codice_fiscale
                                            && <em className="ts-nota manca">senza codice fiscale</em>}
                                    </td>
                                    <td className="destra">{euro(f.totale)}</td>
                                    <td>
                                        <span className={`ts-stato ${s.classe}`}>
                                            <s.Icona size={14} /> {s.testo}
                                        </span>
                                    </td>
                                    <td className="ts-protocollo">{f.ultimo_protocollo || '—'}</td>
                                    <td>
                                        {!f.invio_ts && (
                                            <button
                                                className="ts-btn piccolo"
                                                disabled={inCorso === f.id}
                                                onClick={(e) => { e.stopPropagation(); invia(f.id); }}
                                            >
                                                <Send size={13} />
                                                {inCorso === f.id ? 'invio…' : 'Trasmetti'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
            )}

            {dettaglio && (
                <div className="ts-modale-fondo" onClick={() => setDettaglio(null)}>
                    <div className="ts-modale" onClick={(e) => e.stopPropagation()}>
                        <header>
                            <h3>
                                Fattura {dettaglio.fattura.numero}/{dettaglio.fattura.anno}
                                {' — '}{dettaglio.fattura.intestatario}
                            </h3>
                            <button onClick={() => setDettaglio(null)}><X size={18} /></button>
                        </header>

                        <h4>Può essere trasmessa?</h4>
                        {dettaglio.verifica.trasmissibile ? (
                            <p className="ts-si">Sì, tutti i dati necessari ci sono.</p>
                        ) : (
                            <ul className="ts-problemi">
                                {dettaglio.verifica.problemi.map((p, i) => <li key={i}>{p}</li>)}
                            </ul>
                        )}

                        <h4>Tentativi</h4>
                        {!dettaglio.storico.length ? (
                            <p className="ts-vuoto">Mai tentata.</p>
                        ) : (
                            <ul className="ts-storico">
                                {dettaglio.storico.map((s) => (
                                    <li key={s.id}>
                                        <div className="ts-storico-testa">
                                            <b>{s.operazione}</b>
                                            <span className={`ts-pastiglia ${s.ambiente}`}>
                                                {s.ambiente}
                                            </span>
                                            <em>
                                                {new Date(s.created_at).toLocaleString('it-IT')}
                                                {s.cognome ? ` · ${s.cognome} ${s.nome}` : ''}
                                            </em>
                                        </div>
                                        {s.protocollo && (
                                            <div className="ts-protocollo">
                                                protocollo {s.protocollo}
                                            </div>
                                        )}
                                        {s.errore_rete && (
                                            <div className="ts-msg errore">{s.errore_rete}</div>
                                        )}
                                        {(s.messaggi || []).map((m, i) => (
                                            <div
                                                key={i}
                                                className={`ts-msg ${m.tipo === 'E' ? 'errore' : ''}`}
                                            >
                                                {m.codice && <code>{m.codice}</code>} {m.descrizione}
                                            </div>
                                        ))}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/* ==========================================================
   I resoconti della trasmissione mensile

   Il lavoro pianificato gira il 15 alle 04:00 e nessuno lo guarda: fino
   al 4 settembre 2026 l'esito stava solo nel log del server. Qui si
   legge, e la cosa da leggere e' una sola - quante fatture il periodo
   conteneva e quante sono partite.
   ========================================================== */

const ETICHETTE_ESITO = {
    iniziato: 'Interrotta',
    completato: 'Tutte trasmesse',
    con_errori: 'Con fatture scartate',
    niente_da_fare: 'Niente da trasmettere',
    fallito: 'Non partita',
};

const CLASSI_ESITO = {
    iniziato: 'errore',
    completato: 'ok',
    con_errori: 'attesa',
    niente_da_fare: '',
    fallito: 'errore',
};

const mese = (d) => (d ? new Date(d).toLocaleDateString('it-IT', {
    month: 'long', year: 'numeric',
}) : '—');

const quando = (d) => (d ? new Date(d).toLocaleString('it-IT', {
    dateStyle: 'short', timeStyle: 'short',
}) : '—');

const ResocontiMensili = ({ resoconti, aperto, onApri }) => {
    if (!resoconti?.length) {
        return (
            <section className="ts-resoconti">
                <h2><CalendarClock size={16} /> Trasmissioni mensili</h2>
                <p className="ts-vuoto">
                    Nessuna trasmissione mensile registrata. La prima comparirà qui
                    dopo il 15 del mese.
                </p>
            </section>
        );
    }

    return (
        <section className="ts-resoconti">
            <h2><CalendarClock size={16} /> Trasmissioni mensili</h2>
            <div className="ts-resoconti-elenco">
                {resoconti.map((x) => {
                    const apribile = x.fallite > 0 || x.errore;
                    const espanso = aperto === x.id;
                    return (
                        <div key={x.id} className="ts-resoconto">
                            <button
                                className="ts-resoconto-riga"
                                onClick={() => apribile && onApri(espanso ? null : x.id)}
                                disabled={!apribile}
                            >
                                {apribile
                                    ? (espanso ? <ChevronDown size={14} /> : <ChevronRight size={14} />)
                                    : <span className="ts-resoconto-spazio" />}
                                <b className="ts-resoconto-mese">{mese(x.periodo_dal)}</b>
                                <span className={`ts-resoconto-esito ${CLASSI_ESITO[x.esito] || ''}`}>
                                    {ETICHETTE_ESITO[x.esito] || x.esito}
                                </span>
                                <span className="ts-resoconto-numeri">
                                    {x.esito === 'niente_da_fare'
                                        ? '—'
                                        : `${x.trasmesse} su ${x.da_trasmettere}`}
                                </span>
                                <span className="ts-resoconto-quando">
                                    {quando(x.iniziato_il)}
                                    {!x.automatico && ' · a mano'}
                                    {x.ambiente === 'test' && ' · prova'}
                                </span>
                            </button>

                            {espanso && (
                                <div className="ts-resoconto-dettaglio">
                                    {x.errore && (
                                        <p className="ts-resoconto-errore">{x.errore}</p>
                                    )}
                                    {(x.dettaglio || []).map((d, i) => (
                                        <div key={i} className="ts-resoconto-scartata">
                                            <b>{d.fattura}</b>
                                            <span>{d.motivo}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <small className="ts-resoconti-nota">
                La trasmissione parte da sola il 15 di ogni mese alle 04:00 e riguarda
                le fatture <b>saldate</b> nel mese precedente: vale la data
                dell&apos;incasso, non quella della fattura. Una riga
                «Interrotta» significa che il lavoro è stato interrotto a metà: quelle
                fatture non sono arrivate all&apos;Agenzia.
            </small>
        </section>
    );
};

export default SistemaTS;
